// Analytics & Event Tracking Service
// GA4-compatible event tracking for calculator funnel + Google Ads conversions

import { VERTICALS, resolveVerticalFromPath } from '@/config/contactChannels';

const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA4_MEASUREMENT_ID || process.env.REACT_APP_GA_MEASUREMENT_ID || '';

// Validation-phase flag: adds debug_mode to config + every event so the client
// can verify in GA4 DebugView. Flip REACT_APP_GA4_DEBUG to "false" after
// client sign-off (see ANALYTICS_PLAYBOOK.md change-control).
const GA4_DEBUG = process.env.REACT_APP_GA4_DEBUG === 'true';

// ============================================================
// Google Ads (gtag.js) — AW-18202366224
// Base tag loaded directly in /public/index.html for first-paint
// fire. This module sends `'conversion'` events against it.
//
// VERTICAL SEGREGATION (mandatory per business spec):
//   The site has TWO verticals — Overseas Recruitment ("recruitment")
//   and Study Abroad ("study_abroad"). Each vertical has its OWN
//   set of Google Ads conversion actions so the campaigns can be
//   optimised, budgeted, and reported separately.
//
//   The conversion-label env keys are stored on the vertical config
//   in /app/frontend/src/config/contactChannels.js so the analytics
//   layer auto-picks the right label based on the route the event
//   was fired from. No runtime detection — pure pathname routing.
//
// ENV KEYS (set in /app/frontend/.env after creating conversions in
// the Google Ads UI):
//   Recruitment vertical:
//     REACT_APP_GADS_CONVERSION_LABEL_FORM_SUBMIT_RC
//     REACT_APP_GADS_CONVERSION_LABEL_ELIGIBILITY_RC
//     REACT_APP_GADS_CONVERSION_LABEL_BOOKING_RC
//     REACT_APP_GADS_CONVERSION_LABEL_CTA_CLICK_RC
//     REACT_APP_GADS_CONVERSION_LABEL_WHATSAPP_RC
//     REACT_APP_GADS_CONVERSION_LABEL_CALL_CLICK_RC
//   Study Abroad vertical:
//     REACT_APP_GADS_CONVERSION_LABEL_FORM_SUBMIT_SA
//     REACT_APP_GADS_CONVERSION_LABEL_ELIGIBILITY_SA
//     REACT_APP_GADS_CONVERSION_LABEL_BOOKING_SA
//     REACT_APP_GADS_CONVERSION_LABEL_CTA_CLICK_SA
//     REACT_APP_GADS_CONVERSION_LABEL_WHATSAPP_SA
//     REACT_APP_GADS_CONVERSION_LABEL_CALL_CLICK_SA
//
// If a label is missing, a GA4 event still fires (so funnels work),
// but Google Ads will not register the conversion until the env var
// is filled in. A dev-only warning surfaces locally to help wire-up.
// ============================================================
const GOOGLE_ADS_ID = 'AW-18202366224';

/**
 * Resolve the conversion label for a given (vertical, type) pair.
 * Vertical falls back to the current pathname if not passed.
 */
const resolveAdsLabel = (type, vertical) => {
  const v = vertical || resolveVerticalFromPath((typeof window !== 'undefined' && window.location?.pathname) || '/');
  const conf = VERTICALS[v];
  if (!conf) return '';
  const envKey = conf.adsLabelEnv?.[type];
  if (!envKey) return '';
  // CRA inlines all `REACT_APP_*` env vars at build time, so an
  // indirect lookup against process.env still works correctly here.
  return process.env[envKey] || '';
};

// Track if gtag is available
const isGtagAvailable = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

// Event categories
export const EventCategory = {
  CALCULATOR: 'calculator',
  LEAD: 'lead',
  ENGAGEMENT: 'engagement',
  NAVIGATION: 'navigation',
  CONVERSION: 'conversion'
};

// Standard events for calculator funnel
export const CalculatorEvents = {
  VIEW: 'calculator_view',
  START: 'calculator_start',
  STEP_COMPLETE: 'calculator_step_complete',
  COMPLETE: 'calculator_complete',
  SCORE_CALCULATED: 'score_calculated',
  LEAD_FORM_VIEW: 'lead_form_view',
  LEAD_FORM_START: 'lead_form_start',
  LEAD_FORM_SUBMIT: 'lead_form_submit',
  WHATSAPP_CLICK: 'whatsapp_click',
  BLOG_TO_CALCULATOR: 'blog_to_calculator_click',
  RELATED_ARTICLE_CLICK: 'related_article_click'
};

/**
 * DataLayer -> GA4 bridge
 * ───────────────────────
 * Site events (nav_*, nz_*, mobile_*) are pushed as plain objects to
 * window.dataLayer (GTM-style). Without a GTM container those pushes never
 * reach GA4, so this bridge mirrors each custom dataLayer event to
 * gtag('event', ...). CHANGE-CONTROL: if a GTM container is ever installed,
 * set REACT_APP_DL_GTAG_BRIDGE="false" to prevent double-counting.
 */
const DL_BRIDGE_DISABLED = process.env.REACT_APP_DL_GTAG_BRIDGE === 'false';

const isCustomDLEvent = (obj) =>
  obj && typeof obj === 'object' && !Array.isArray(obj) &&
  typeof obj.event === 'string' && !obj.event.startsWith('gtm.');

const mirrorToGa4 = (obj) => {
  const { event, ...params } = obj;
  delete params['gtm.uniqueEventId'];
  window.gtag('event', event, { ...params, ...(GA4_DEBUG ? { debug_mode: true } : {}) });
};

const installDataLayerBridge = () => {
  if (DL_BRIDGE_DISABLED || window.__dlBridgeInstalled) return;
  window.__dlBridgeInstalled = true;
  const dl = window.dataLayer;
  const pending = dl.filter(isCustomDLEvent);
  const origPush = dl.push.bind(dl);
  dl.push = (...args) => {
    const res = origPush(...args);
    args.forEach((obj) => { if (isCustomDLEvent(obj)) mirrorToGa4(obj); });
    return res;
  };
  pending.forEach(mirrorToGa4);
};

/**
 * Initialize GA4 tracking
 */
export const initAnalytics = () => {
  if (!GA_MEASUREMENT_ID) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('GA4 Measurement ID not configured');
    }
    return;
  }

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
  }

  // The gtag.js library is already loaded once in /public/index.html (Google
  // Ads base tag). Loading it a second time would create a duplicate GA
  // instance, so only inject if genuinely absent.
  const hasLib =
    document.getElementById('ga-script') ||
    document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
  if (!hasLib) {
    const script = document.createElement('script');
    script.id = 'ga-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  // Single-instance guard: config exactly once (send_page_view fires the one
  // initial page_view; SPA route changes are tracked by GA4 Enhanced
  // Measurement history events — no manual page_view calls, no duplicates).
  if (window.__ga4Configured) return;
  window.__ga4Configured = true;
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: true,
    cookie_flags: 'SameSite=None;Secure',
    ...(GA4_DEBUG ? { debug_mode: true } : {}),
  });

  installDataLayerBridge();
};

/**
 * Track custom event
 * @param {string} eventName - Name of the event
 * @param {object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  // Always log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Analytics] ${eventName}`, params);
  }

  // Send to GA4
  if (isGtagAvailable()) {
    window.gtag('event', eventName, {
      ...params,
      event_category: params.category || EventCategory.ENGAGEMENT,
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Lead Quality Engine — phone validation events (ZERO PII).
 * Only anonymous metadata: ISO country code + validation_result.
 * @param {boolean} passed
 * @param {{country?: string, validationResult?: string}} meta
 */
/**
 * Secure Lead Pipeline funnel events (ZERO PII).
 * Allowed params: destination, education, country, channel, reason, verification_result.
 */
export const trackFunnelEvent = (eventName, params = {}) => {
  trackEvent(eventName, { category: EventCategory.LEAD, ...params });
};

export const trackPhoneValidation = (passed, { country = '', validationResult = '' } = {}) => {
  trackEvent(passed ? 'phone_validation_passed' : 'phone_validation_failed', {
    category: EventCategory.LEAD,
    country,
    validation_result: validationResult,
  });
};

/**
 * Track calculator view
 * @param {string} calculatorId - Calculator identifier (e.g., 'canada', 'australia')
 * @param {string} country - Target country
 * @param {string} sourcePage - Source page URL
 */
export const trackCalculatorView = (calculatorId, country, sourcePage) => {
  trackEvent(CalculatorEvents.VIEW, {
    category: EventCategory.CALCULATOR,
    calculator_id: calculatorId,
    country: country,
    source_page: sourcePage,
    page_location: window.location.href
  });
};

/**
 * Track calculator start (user begins entering data)
 * @param {string} calculatorId - Calculator identifier
 * @param {string} country - Target country
 */
export const trackCalculatorStart = (calculatorId, country) => {
  trackEvent(CalculatorEvents.START, {
    category: EventCategory.CALCULATOR,
    calculator_id: calculatorId,
    country: country
  });
};

/**
 * Track calculator step completion
 * @param {string} calculatorId - Calculator identifier
 * @param {number} stepNumber - Current step number
 * @param {string} stepName - Step name/label
 */
export const trackCalculatorStep = (calculatorId, stepNumber, stepName) => {
  trackEvent(CalculatorEvents.STEP_COMPLETE, {
    category: EventCategory.CALCULATOR,
    calculator_id: calculatorId,
    step_number: stepNumber,
    step_name: stepName
  });
};

/**
 * Track calculator completion with score
 * @param {string} calculatorId - Calculator identifier
 * @param {string} country - Target country
 * @param {number} score - Calculated score
 * @param {number} maxScore - Maximum possible score
 * @param {string} eligibility - Eligibility status
 */
export const trackScoreCalculated = (calculatorId, country, score, maxScore, eligibility) => {
  trackEvent(CalculatorEvents.SCORE_CALCULATED, {
    category: EventCategory.CALCULATOR,
    calculator_id: calculatorId,
    country: country,
    score: score,
    max_score: maxScore,
    score_percentage: Math.round((score / maxScore) * 100),
    eligibility_status: eligibility
  });

  // Also track as conversion event
  trackEvent(CalculatorEvents.COMPLETE, {
    category: EventCategory.CALCULATOR,
    calculator_id: calculatorId,
    country: country,
    value: score
  });
};

/**
 * Track lead form view
 * @param {string} calculatorId - Calculator identifier
 * @param {string} sourcePage - Source page
 */
export const trackLeadFormView = (calculatorId, sourcePage) => {
  trackEvent(CalculatorEvents.LEAD_FORM_VIEW, {
    category: EventCategory.LEAD,
    calculator_id: calculatorId,
    source_page: sourcePage
  });
};

/**
 * Track lead form start (user begins filling form)
 * @param {string} calculatorId - Calculator identifier
 */
export const trackLeadFormStart = (calculatorId) => {
  trackEvent(CalculatorEvents.LEAD_FORM_START, {
    category: EventCategory.LEAD,
    calculator_id: calculatorId
  });
};

/**
 * Track lead form submission
 * @param {string} calculatorId - Calculator identifier
 * @param {string} country - Target country
 * @param {number} score - Calculated score (if applicable)
 * @param {string} sourcePage - Source page URL
 */
export const trackLeadSubmission = (calculatorId, country, score, sourcePage) => {
  trackEvent(CalculatorEvents.LEAD_FORM_SUBMIT, {
    category: EventCategory.LEAD,
    calculator_id: calculatorId,
    country: country,
    score: score,
    source_page: sourcePage,
    conversion: true
  });
};

// ============================================================
// GOOGLE ADS CONVERSION TRACKING
// ============================================================
//
// `trackConversion` is the single entry point for sending a
// conversion event to Google Ads (AW-18202366224). It ALSO
// emits a parallel GA4 event for funnel analytics, so we never
// have to fire two helpers from a component.
//
// Usage from a React component:
//   import { trackConversion } from '@/services/analytics';
//   trackConversion('form_submit', { source: 'why-zafco-inline', value: 50 });
//
// Parameters:
//   type    string  — one of CONVERSION_LABELS keys
//                     (form_submit | eligibility_submit |
//                      counsellor_booking | primary_cta_click |
//                      whatsapp_click)
//   payload object  — { source, value, currency, transaction_id, ... }
//
// Notes on `value`:
//   • Pass a numeric estimated lead value (in INR) where you can —
//     it lets Google Ads optimise toward higher-LTV leads.
//   • Default currency is INR — override per-call if needed.
//   • If `transaction_id` is omitted, we generate one per call
//     to satisfy Google Ads' deduplication requirement.
// ============================================================

const genTxnId = () => {
  try {
    return (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `zaf-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  } catch (_err) {
    return `zaf-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
};

/**
 * Fire a Google Ads conversion event (and a parallel GA4 event).
 * Vertical-aware: the conversion label and GA4 event name are
 * auto-selected based on the current route (recruitment vs study_abroad).
 *
 * @param {string} type - Conversion type key (must exist in `adsLabelEnv` of each vertical).
 * @param {object} payload - Optional metadata: { source, value, currency, transaction_id, vertical, ...extra }.
 * @returns {boolean} true if the event was pushed to gtag's Ads send_to.
 */
export const trackConversion = (type, payload = {}) => {
  const {
    source = window.location?.pathname || 'unknown',
    value = 0,
    currency = 'INR',
    transaction_id,
    vertical: overrideVertical,
    ...extra
  } = payload;

  // Vertical is resolved STATICALLY from the current pathname. No UA,
  // referrer, or visitor-class detection — same outcome for every visitor
  // (real users and Google's Ads crawler alike).
  const vertical = overrideVertical || resolveVerticalFromPath((typeof window !== 'undefined' && window.location?.pathname) || '/');

  // GA4 event names are vertical-suffixed so funnels segment cleanly
  // in GA4 Explore / Audiences without any UI configuration.
  trackEvent(`conversion_${type}_${vertical}`, {
    category: EventCategory.CONVERSION,
    conversion_type: type,
    vertical,
    source,
    value,
    currency,
    ...extra,
  });

  // Google Ads requires the `send_to` string "AW-<id>/<label>".
  // Each vertical has its OWN conversion label (per business spec).
  const label = resolveAdsLabel(type, vertical);
  if (!label) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `[Analytics] Google Ads label not configured for vertical="${vertical}" type="${type}". ` +
        `Set ${VERTICALS[vertical]?.adsLabelEnv?.[type] || `REACT_APP_GADS_CONVERSION_LABEL_${type.toUpperCase()}_${vertical === 'study_abroad' ? 'SA' : 'RC'}`} in /app/frontend/.env to enable Ads attribution.`
      );
    }
    return false;
  }

  if (!isGtagAvailable()) return false;

  window.gtag('event', 'conversion', {
    send_to: `${GOOGLE_ADS_ID}/${label}`,
    value,
    currency,
    transaction_id: transaction_id || genTxnId(),
  });
  return true;
};

/**
 * Convenience: high-intent form submission (eligibility / consultation /
 * contact / inline lead-capture). Pass `source` to attribute campaigns.
 */
export const trackFormSubmit = (source, extra = {}) =>
  trackConversion('form_submit', { source, value: 50, ...extra });

/**
 * Convenience: a Profile Evaluation / Eligibility-quiz lead.
 * Higher value than a generic form because the user shared profile depth.
 */
export const trackEligibilitySubmit = (source, extra = {}) =>
  trackConversion('eligibility_submit', { source, value: 100, ...extra });

/**
 * Convenience: booked-counsellor-slot signal (highest intent).
 */
export const trackCounsellorBooking = (source, extra = {}) =>
  trackConversion('counsellor_booking', { source, value: 200, ...extra });

/**
 * Convenience: primary CTA click — e.g. "Check Eligibility" buttons
 * on the partner directory, "Book Strategy Session" hero buttons.
 * Treated as a mid-intent conversion event so Smart Bidding has signal.
 */
export const trackCtaClick = (ctaId, extra = {}) =>
  trackConversion('primary_cta_click', { source: ctaId, value: 10, ...extra });

/**
 * Convenience: tel: click — fires when a user taps any "Call Us"
 * button or phone link. Mid-intent signal, scoped per vertical.
 */
export const trackCallClick = (context, extra = {}) =>
  trackConversion('call_click', { source: context, value: 30, ...extra });

/**
 * Track WhatsApp click
 * @param {string} sourcePage - Source page
 * @param {string} context - Context (e.g., 'header', 'footer', 'calculator_result')
 */
export const trackWhatsAppClick = (sourcePage, context) => {
  trackEvent(CalculatorEvents.WHATSAPP_CLICK, {
    category: EventCategory.ENGAGEMENT,
    source_page: sourcePage,
    context: context
  });
  // Mid-intent signal for Google Ads if configured
  trackConversion('whatsapp_click', { source: `${sourcePage}|${context}`, value: 20 });
};

/**
 * Track blog to calculator navigation
 * @param {string} blogSlug - Blog post slug
 * @param {string} calculatorId - Target calculator
 */
export const trackBlogToCalculator = (blogSlug, calculatorId) => {
  trackEvent(CalculatorEvents.BLOG_TO_CALCULATOR, {
    category: EventCategory.NAVIGATION,
    blog_slug: blogSlug,
    calculator_id: calculatorId,
    source_page: window.location.href
  });
};

/**
 * Track related article click
 * @param {string} fromPage - Current page
 * @param {string} toArticle - Target article slug
 */
export const trackRelatedArticleClick = (fromPage, toArticle) => {
  trackEvent(CalculatorEvents.RELATED_ARTICLE_CLICK, {
    category: EventCategory.NAVIGATION,
    from_page: fromPage,
    to_article: toArticle
  });
};

/**
 * Track page view (for SPA navigation)
 * @param {string} pagePath - Page path
 * @param {string} pageTitle - Page title
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (isGtagAvailable()) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
};

export default {
  initAnalytics,
  trackEvent,
  trackCalculatorView,
  trackCalculatorStart,
  trackCalculatorStep,
  trackScoreCalculated,
  trackLeadFormView,
  trackLeadFormStart,
  trackLeadSubmission,
  trackWhatsAppClick,
  trackBlogToCalculator,
  trackRelatedArticleClick,
  trackPageView,
  trackConversion,
  trackFormSubmit,
  trackEligibilitySubmit,
  trackCounsellorBooking,
  trackCtaClick,
  trackCallClick,
  CalculatorEvents,
  EventCategory
};
